import { Component, OnInit, AfterViewInit, HostListener } from '@angular/core';
import { SourceService } from '../../data-access-layer/services/sources.service';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { forkJoin } from 'rxjs';
import { PythonService } from '../../data-access-layer/services/python.service';
import { GetLogsDataSourceResponse } from '../../data-access-layer/interfaces/source.interface';
import { UserSessionStorageService } from '../../data-access-layer/services/user-sessions.service';

@Component({
  selector: 'app-changes-sources',
  templateUrl: '../templates/changes-sources.component.html',
  styleUrls: ['../templates/styles/changes-sources.component.css'],
  standalone: false
})
export class ChangesSourcesComponent implements OnInit, AfterViewInit {
  changes: any[] = [];
  filteredChanges: any[] = [];
  dataSources: any[] = [];
  allDataSources: any[] = [];
  selectedDataSource: any;

  dateRange: Date[] = [];
  startDate = '';
  endDate = '';
  useDate = true;

  open = false;
  openChangeTypes = false;
  openSchemas = false;
  openObjectTypes = false;
  openActuality = false;
  openObjectNames = false;
  openDateTimes = false;

  isShowBI = false;
  acceptedFilter: 'all' | 'accepted' | 'not_accepted' = 'all';
  objectNameSearch = '';

  // Уникальные значения для фильтров (из данных)
  uniqueSchemas: string[] = [];
  uniqueObjectNames: string[] = [];
  uniqueDateTimes: string[] = [];

  // Полный список типов объектов для dropdown
  readonly allObjectTypes = ['table', 'function', 'procedure', 'view', 'report'];
  readonly allObjectActualities = ['yes', 'no'];

  // Выбранные значения фильтров
  selectedSchemas = new Set<string>();
  selectedObjectTypes = new Set<string>();
  selectedObjectActualities = new Set<string>();
  selectedObjectNames = new Set<string>();
  selectedDateTimes = new Set<string>();

  private objectTypeKeyMap: Record<string, string> = {
    table: 'changesSources.objectTypes.table',
    view: 'changesSources.objectTypes.view',
    procedure: 'changesSources.objectTypes.procedure',
    function: 'changesSources.objectTypes.function',
    report: 'changesSources.objectTypes.report'
  };

  private actualityKeyMap: Record<string, string> = {
    yes: 'changesSources.actualities.yes',
    no: 'changesSources.actualities.no'
  };


  // Пагинация
  currentPage = 1;
  pageSize = 50;

  get totalPages(): number {
    return Math.ceil(this.filteredChanges.length / this.pageSize) || 1;
  }

  get paginatedChanges(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredChanges.slice(start, start + this.pageSize);
  }

  get startIndex(): number {
    return (this.currentPage - 1) * this.pageSize;
  }

  readonly changeTypesMeta = [
    { key: 'new', label: '', selected: true },
    { key: 'updated', label: '', selected: true },
    { key: 'deleted', label: '', selected: true }
  ];
 
  readonly changeTypesCrawlImg = [
    { key: 'img_crawled', label: '', selected: true },
    { key: 'img_crawled_error', label: '', selected: true }
  ];

  get changeTypes() {
    return [...this.changeTypesMeta, ...this.changeTypesCrawlImg];
  }

  flatpickrInstance: any = null;

  onFlatpickrReady(selectedDates: Date[], dateString: string, instance: any): void {
    this.flatpickrInstance = instance;
    this.addNoDateButton(instance);
    if (this.dateRange && this.dateRange.length >= 2) {
      setTimeout(() => {
        if (instance && (!selectedDates || selectedDates.length === 0)) {
          instance.setDate(this.dateRange, false);
          if (instance.altInput) {
            instance.redraw();
          }
        }
      }, 200);
    }
  }

  private pendingDatasourceId: number | null = null;
  private pendingDatasourceType: string | null = null;

  constructor(
    private sourceService: SourceService,
    private router: Router,
    private route: ActivatedRoute,
    public transloco: TranslocoService,
    private pythonService: PythonService,
    private sessionService: UserSessionStorageService
  ) {}

  ngOnInit(): void {
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    this.dateRange = [yesterday, today];
    this.startDate = this.formatDate(yesterday);
    this.endDate = this.formatDate(today);
    this.loadChangeTypesLabels();

    // Читаем query params синхронно (один раз при инициализации)
    const queryParams = this.route.snapshot.queryParams;
    if (queryParams['datasourceId']) {
      this.pendingDatasourceId = +queryParams['datasourceId'];
      this.pendingDatasourceType = queryParams['datasourceType'] || 'dwh';
    }

    forkJoin({
      dwh: this.sourceService.getDWHSources(),
      report: this.sourceService.getBISources()
    }).subscribe(({ dwh, report }) => {
      this.allDataSources = [
        ...dwh.map(ds => ({ type: 'dwh', id: ds.id, name: ds.dataSourceName })),
        ...report.map(ds => ({ type: 'report', id: ds.id, name: ds.dataSourceName }))
      ];
      
      if (this.pendingDatasourceId !== null) {
        this.isShowBI = this.pendingDatasourceType === 'report';
        // Пропускаем автоматическую загрузку данных, т.к. выберем конкретный источник ниже
        this.filterDataSources(true);
        
        const targetSource = this.allDataSources.find(
          ds => ds.id === this.pendingDatasourceId && ds.type === this.pendingDatasourceType
        );
        if (targetSource) {
          this.selectedDataSource = targetSource;
          this.getLogData(true);
        }
        
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {},
          replaceUrl: true
        });
        
        this.pendingDatasourceId = null;
        this.pendingDatasourceType = null;
      } else {
        this.filterDataSources();
      }
    });

    this.transloco.langChanges$.subscribe(() => this.loadChangeTypesLabels());
  }

  ngAfterViewInit(): void {
    // Дополнительная проверка на случай, если flatpickr уже инициализирован
    setTimeout(() => {
      if (this.flatpickrInstance && this.dateRange && this.dateRange.length >= 2) {
        const currentDates = this.flatpickrInstance.selectedDates;
        if (!currentDates || currentDates.length === 0) {
          this.flatpickrInstance.setDate(this.dateRange, false);
        }
      }
    }, 500);
  }

  private loadChangeTypesLabels(): void {
    const updateLabels = (types: any[]) => types.forEach(ct => ct.label = this.getChangeTypeLabel(ct.key));
    updateLabels(this.changeTypesMeta);
    updateLabels(this.changeTypesCrawlImg);
  }

  private getChangeTypeLabel(key: string): string {
    return this.transloco.translate(`changesSources.changeTypes.${key}`) || '';
  }

  onDateRangeChange(selectedDates: Date[]): void {
    if (selectedDates?.length >= 1) {
      this.dateRange = selectedDates;
      this.startDate = this.formatDate(selectedDates[0]);
      this.endDate = selectedDates.length >= 2 ? this.formatDate(selectedDates[1]) : this.startDate;
      this.useDate = true;
    } else {
      this.startDate = '';
      this.endDate = '';
    }
    this.getLogData(true);
  }

  private formatDate(date: Date): string {
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  toggleIsBI(isBI: boolean): void {
    this.isShowBI = isBI;
    this.changes = [];
    this.selectedDataSource = null;
    this.filterDataSources();
  }

  private filterDataSources(skipAutoLoad: boolean = false): void {
    const targetType = this.isShowBI ? 'report' : 'dwh';
    this.dataSources = this.allDataSources.filter(ds => ds.type === targetType);

    if (this.dataSources.length > 0) {
      // Если skipAutoLoad = true, не устанавливаем selectedDataSource и не вызываем getLogData
      // (используется когда источник будет выбран вручную после фильтрации)
      if (!skipAutoLoad) {
        this.selectedDataSource = this.dataSources[0];
        this.getLogData(true); // Сбрасываем фильтры при смене типа источника
      }
    }
  }

  selectDataSource(ds: string): void {
    this.selectedDataSource = this.dataSources.find(x => x.name === ds);
    this.getLogData(true); // Сбрасываем фильтры при выборе другого источника
  }

  toggleOpen(): void {
    this.closeAllDropdowns();
    this.open = !this.open;
  }

  toggleChangeTypesOpen(): void {
    this.closeAllDropdowns();
    this.openChangeTypes = !this.openChangeTypes;
  }

  toggleSchemasOpen(): void {
    this.closeAllDropdowns();
    this.openSchemas = !this.openSchemas;
  }

  toggleObjectTypesOpen(): void {
    this.closeAllDropdowns();
    this.openObjectTypes = !this.openObjectTypes;
  }

  toggleObjectActualityOpen(): void {
    this.closeAllDropdowns();
    this.openActuality = !this.openActuality;
  }

  toggleObjectNamesOpen(): void {
    this.closeAllDropdowns();
    this.openObjectNames = !this.openObjectNames;
  }

  private closeAllDropdowns(): void {
    this.open = false;
    this.openChangeTypes = false;
    this.openActuality = false;
    this.openSchemas = false;
    this.openObjectTypes = false;
    this.openObjectNames = false;
    this.openDateTimes = false;
  }

  toggleDateTimesOpen(): void {
    this.closeAllDropdowns();
    this.openDateTimes = !this.openDateTimes;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!(event.target as HTMLElement).closest('.dropdown')) {
      this.closeAllDropdowns();
    }
  }

  toggleChangeType(changeType: any, event: Event): void {
    event.stopPropagation();
    changeType.selected = !changeType.selected;
    this.getLogData(false);
  }

  getSelectedChangeTypesLabel(): string {
    const allTypes = this.changeTypes;
    const selected = allTypes.filter(ct => ct.selected);
    if (selected.length === 0 || selected.length === allTypes.length) {
      return this.transloco.translate('changesSources.allTypes');
    }
    return selected.map(ct => ct.label).join(', ');
  }

  private applyFilters(): void {
    let filtered = [...this.changes];

    if (this.selectedSchemas.size > 0) {
      filtered = filtered.filter(x => this.selectedSchemas.has(x.schemaName));
    }
    if (this.selectedObjectTypes.size > 0) {
      filtered = filtered.filter(x => this.selectedObjectTypes.has(x.objectType));
    }
    if (this.selectedObjectActualities.size > 0) {
      filtered = filtered.filter(x => this.selectedObjectActualities.has(x.is_actual ? 'yes' : 'no'));
    }
    if (this.selectedObjectNames.size > 0) {
      filtered = filtered.filter(x => this.selectedObjectNames.has(x.objectName));
    }
    if (this.selectedDateTimes.size > 0) {
      filtered = filtered.filter(x => this.selectedDateTimes.has(this.extractDateFromDateTime(x.dateTime)));
    }
    if (this.objectNameSearch?.trim()) {
      const search = this.objectNameSearch.toLowerCase().trim();
      filtered = filtered.filter(x => x.objectName?.toLowerCase().includes(search));
    }

    this.filteredChanges = filtered;
    this.currentPage = 1;
  }

  private extractDateFromDateTime(dateTime: string): string {
    if (!dateTime) return '';
    const date = new Date(dateTime);
    return this.formatDate(date);
  }

  getLogData(resetFilters: boolean = false): void {
    const savedSchemas = resetFilters ? new Set<string>() : new Set(this.selectedSchemas);
    const savedObjectTypes = resetFilters ? new Set<string>() : new Set(this.selectedObjectTypes);
    const savedObjectNames = resetFilters ? new Set<string>() : new Set(this.selectedObjectNames);
    const savedDateTimes = resetFilters ? new Set<string>() : new Set(this.selectedDateTimes);
    const savedObjectActualities = resetFilters ? new Set<string>() : new Set(this.selectedObjectActualities);
    const savedObjectNameSearch = resetFilters ? '' : this.objectNameSearch;

    if (!this.selectedDataSource) {
      this.changes = [];
      this.filteredChanges = [];
      this.resetFilters();
      return;
    }

    this.changes = [];

    const selectedMeta = this.changeTypesMeta.filter(ct => ct.selected);
    /*const selectedAnalyze = this.changeTypesAnalyze.filter(ct => ct.selected);*/
    const selectedCrawlImg = this.changeTypesCrawlImg.filter(ct => ct.selected);

    const baseRequest: any = {
      datasource_id: this.selectedDataSource.id,
      datasource_type: this.selectedDataSource.type
    };

    if (this.useDate && this.startDate) {
      baseRequest.start_dt = this.startDate;
      baseRequest.end_dt = this.endDate || this.startDate;
    }

    const requests: Record<string, any> = {};

    if (selectedMeta.length > 0) {
      requests['extractMeta'] = this.pythonService.getLogDatasource({
        ...baseRequest,
        ...(selectedMeta.length < this.changeTypesMeta.length ? { action_types: selectedMeta.map(ct => ct.key) } : {})
      });
    }

    if (this.acceptedFilter !== 'all') {

      requests['extractMeta'] = this.pythonService.getLogDatasource({
        ...baseRequest,
        ...{
          include_accepted: this.acceptedFilter !== 'not_accepted',
          only_accepted: this.acceptedFilter === 'accepted'
        }
      });
    }

    if (this.selectedDataSource.type === 'report' && selectedCrawlImg.length > 0) {
      requests['detailsCrawlImg'] = this.pythonService.getLogDetailsCrawlImg({
        ...baseRequest,
        ...(selectedCrawlImg.length < this.changeTypesCrawlImg.length ? { action_types: selectedCrawlImg.map(ct => ct.key) } : {})
      });
    }

    // Если нет запросов — очищаем данные, но сохраняем фильтры
    if (Object.keys(requests).length === 0) {
      this.changes = [];
      this.filteredChanges = [];
      this.updateUniqueValues();
      this.selectedSchemas.clear();
      this.selectedObjectTypes.clear();
      this.selectedObjectNames.clear();
      this.selectedObjectActualities.clear();
      this.selectedDateTimes.clear();
      this.objectNameSearch = savedObjectNameSearch;
      return;
    }

    forkJoin(requests).subscribe((responses: any) => {
      if (this.acceptedFilter !== 'all') {
        // Только analyze данные при фильтре принятых/непринятых
        this.processResponse(responses['extractMeta'], 'extract_meta');
      } else {
        if (responses['extractMeta']) {
          this.processResponse(responses['extractMeta'], 'extract_meta');
        }
        if (responses['detailsCrawlImg']) {
          this.processResponse(responses['detailsCrawlImg'], 'crawl_images');
        }
      }

      this.updateUniqueValues();
      
      // Восстанавливаем фильтры, которые существуют в новых данных
      this.selectedSchemas.clear();
      savedSchemas.forEach(schema => {
        if (this.uniqueSchemas.includes(schema)) {
          this.selectedSchemas.add(schema);
        }
      });
      
      this.selectedObjectTypes.clear();
      savedObjectTypes.forEach(type => {
        if (this.allObjectTypes.includes(type)) {
          this.selectedObjectTypes.add(type);
        }
      });
      
      this.selectedObjectNames.clear();
      savedObjectNames.forEach(name => {
        if (this.uniqueObjectNames.includes(name)) {
          this.selectedObjectNames.add(name);
        }
      });
      
      this.selectedDateTimes.clear();
      savedDateTimes.forEach(date => {
        if (this.uniqueDateTimes.includes(date)) {
          this.selectedDateTimes.add(date);
        }
      });
      
      this.selectedObjectActualities.clear();
      savedObjectActualities.forEach(actuality => {
        if (this.allObjectActualities.includes(actuality)) {
          this.selectedObjectActualities.add(actuality);
        }
      });
      
      this.objectNameSearch = savedObjectNameSearch;
      
      this.applyFilters();
    });
  }

  private resetFilters(): void {
    this.selectedSchemas.clear();
    this.selectedObjectTypes.clear();
    this.selectedObjectNames.clear();
    this.selectedDateTimes.clear();
    this.selectedObjectActualities.clear();
    this.currentPage = 1;
  }

  private processResponse(response: GetLogsDataSourceResponse | null, actionType: string): void {
    if (!response) return;

    const config: Record<string, { types: any[], keys: string[] }> = {
      'extract_meta': { types: this.changeTypesMeta, keys: ['new', 'updated', 'deleted'] },
      
      'crawl_images': { types: this.changeTypesCrawlImg, keys: ['img_crawled', 'img_crawled_error'] }
    };

    const { types, keys } = config[actionType] || { types: this.changeTypes, keys: [] };

    keys.forEach(typeKey => {
      const changeType = types.find(ct => ct.key === typeKey);
      if (!changeType?.selected) return;

      const changeData = response[typeKey as keyof GetLogsDataSourceResponse];
      if (!changeData) return;

      const label = this.getChangeTypeLabel(typeKey);

      if (typeKey === 'img_crawled_error') {
        const errorData = changeData as any;
        if (errorData.report?.length > 0) {
          this.pushChanges(errorData.report, label, 'report', actionType);
        }
      } else {
        this.allObjectTypes.forEach(objType => {
          const items = (changeData as Record<string, any[]>)[objType] ?? [];
          if (items.length > 0) {
            this.pushChanges(items, label, objType, actionType);
          }
        });
      }
    });
  }

  private pushChanges(items: any[], type: string, objectType: string, actionType: string): void {
    items.forEach(x => {
      this.changes.push({
        id: x.id,
        schemaName: x.schema_name,
        schemaId: x.schema_id,
        objectName: x.name,
        objectType,
        type,
        dateTime: x.dt,
        actionType,
        is_accepted: x.is_accepted !== undefined ? x.is_accepted : null,
        is_actual: x.is_actual !== undefined ? x.is_actual : null
      });
    });
  }

  private updateUniqueValues(): void {
    const schemas = new Set<string>();
    const objectNames = new Set<string>();
    const dateTimes = new Set<string>();

    this.changes.forEach(change => {
      if (change.schemaName) schemas.add(change.schemaName);
      if (change.objectName) objectNames.add(change.objectName);
      if (change.dateTime) {
        const dateOnly = this.extractDateFromDateTime(change.dateTime);
        if (dateOnly) dateTimes.add(dateOnly);
      }
    });

    this.uniqueSchemas = Array.from(schemas).sort();
    this.uniqueObjectNames = Array.from(objectNames).sort();
    this.uniqueDateTimes = Array.from(dateTimes).sort().reverse();
  }

  toggleSchema(schema: string, event: Event): void {
    event.stopPropagation();
    this.toggleSetValue(this.selectedSchemas, schema);
    this.applyFilters();
  }

  toggleObjectType(objectType: string, event: Event): void {
    event.stopPropagation();
    this.toggleSetValue(this.selectedObjectTypes, objectType);
    this.applyFilters();
  }

  toggleActualityType(objectActuality: string, event: Event): void {
    event.stopPropagation();
    this.toggleSetValue(this.selectedObjectActualities, objectActuality);
    this.applyFilters();
  }

  toggleObjectName(objectName: string, event: Event): void {
    event.stopPropagation();
    this.toggleSetValue(this.selectedObjectNames, objectName);
    this.applyFilters();
  }

  toggleDateTime(dateTime: string, event: Event): void {
    event.stopPropagation();
    this.toggleSetValue(this.selectedDateTimes, dateTime);
    this.applyFilters();
  }

  private toggleSetValue(set: Set<string>, value: string): void {
    if (set.has(value)) {
      set.delete(value);
    } else {
      set.add(value);
    }
  }

  getSelectedSchemasLabel(): string {
    if (this.selectedSchemas.size === 0 || this.selectedSchemas.size === this.uniqueSchemas.length) {
      return this.transloco.translate('changesSources.allSchemas');
    }
    return Array.from(this.selectedSchemas).join(', ');
  }

  getSelectedObjectTypesLabel(): string {
    if (
      this.selectedObjectTypes.size === 0 ||
      this.selectedObjectTypes.size === this.allObjectTypes.length
    ) {
      return this.transloco.translate('changesSources.allTypes');
    }

    return Array.from(this.selectedObjectTypes)
      .map(type => this.transloco.translate(this.objectTypeKeyMap[type] ?? type))
      .join(', ');
  }


  getSelectedObjectActualityLabel(): string {
    if (
      this.selectedObjectActualities.size === 0 ||
      this.selectedObjectActualities.size === this.allObjectActualities.length
    ) {
      return this.transloco.translate('changesSources.all');
    }

    return Array.from(this.selectedObjectActualities)
      .map(a => this.transloco.translate(this.actualityKeyMap[a] ?? a))
      .join(', ');
  }


  getSelectedObjectNamesLabel(): string {
    if (this.selectedObjectNames.size === 0 || this.selectedObjectNames.size === this.uniqueObjectNames.length) {
      return this.transloco.translate('changesSources.all');
    }
    return Array.from(this.selectedObjectNames).join(', ');
  }

  getSelectedDateTimesLabel(): string {
    if (this.selectedDateTimes.size === 0 || this.selectedDateTimes.size === this.uniqueDateTimes.length) {
      return this.transloco.translate('changesSources.all');
    }
    return Array.from(this.selectedDateTimes).map(d => this.formatDisplayDate(d)).join(', ');
  }

  formatDisplayDate(dateStr: string): string {
    const parts = dateStr.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    return dateStr;
  }

  getObjectTypeLabel(objectType: string): string {
    return this.transloco.translate(`changesSources.objectTypes.${objectType}`) || objectType;
  }

  getObjectActualityLabel(objectActuality: string): string {
    return this.transloco.translate(`changesSources.actualities.${objectActuality}`) || objectActuality;
  }

  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  prevPage(): void {
    this.goToPage(this.currentPage - 1);
  }

  nextPage(): void {
    this.goToPage(this.currentPage + 1);
  }

  onPageInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const page = parseInt(input.value, 10);
    if (!isNaN(page)) {
      this.goToPage(page);
    }
    input.value = this.currentPage.toString();
  }

  onObjectNameSearchChange(): void {
    this.applyFilters();
  }

  clearObjectNameSearch(): void {
    this.objectNameSearch = '';
    this.applyFilters();
  }

  goToObject(change: any): void {
    const isBI = this.selectedDataSource.type === 'report';

    this.sessionService.setItem('currentElement', {
      id: change.id,
      name: change.objectName,
      schema: change.schemaName,
      schemaId: change.schemaId || 0,
      dataSourceId: this.selectedDataSource.id
    });
    this.sessionService.setItem('currentDataSource', {
      id: this.selectedDataSource.id,
      dataSourceName: this.selectedDataSource.name
    });
    this.sessionService.setItem('elementType', this.capitalizeFirstLetter(change.objectType));
    this.sessionService.setItem('isBI', isBI);

    this.router.navigate(['/content']);
  }

  private capitalizeFirstLetter(str: string): string {
    return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  }

  clearDate(): void {
    this.useDate = false;
    this.dateRange = [];
    this.startDate = '';
    this.endDate = '';
    this.getLogData(true);
  }

  private addNoDateButton(instance: any): void {
    const container = instance.calendarContainer;
    if (!container) return;

    container.querySelector('.flatpickr-no-date-btn')?.remove();

    const btn = document.createElement('button');
    btn.textContent = this.transloco.translate('changesSources.noDate');
    btn.className = 'flatpickr-no-date-btn';
    btn.type = 'button';
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.clearDate();
      instance.close();
    });

    container.appendChild(btn);
  }
}
